import type { ScheduleBuffer } from "@gettactic/api";
import {
  EMPTY_BUFFER,
  ReservationBufferFields,
  ReservationBufferSummary
} from "@gettactic/components/src/components/reservation-buffer/ReservationBufferFields";
import { isReservationBufferEnabled } from "@gettactic/helpers/src/featureFlags";
import { useAuthenticated } from "@gettactic/hooks";
import { addMinutes } from "date-fns";
import React from "react";
import { useWatch } from "react-hook-form";
import { InputLabel } from "@/features/reserve/components/components/InputLabel/InputLabel";
import { useReserveForm } from "@/features/reserve/components/Reserve/ReserveFormProvider";

/**
 * Preparation and cleanup time for a room booking. Rooms only: a desk has
 * nothing to set up, and the office buffer already covers the gap desks need
 * between bookings.
 */
export const ReserveFormBuffer: React.FC = () => {
  const { form, isMeetingRoom, startTime, durationMins } = useReserveForm();
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  // `useWatch` rather than `form.watch()` in render: it keeps the re-render to
  // this control instead of the whole form, and — unlike a `watch()` call sitting
  // after the early return below — it subscribes the same fields on every render.
  const watchedBuffer = useWatch({ control: form.control, name: "buffer" });
  const watchedTime = useWatch({ control: form.control, name: "time" });
  const watchedDuration = useWatch({ control: form.control, name: "duration" });

  if (!isMeetingRoom || !isReservationBufferEnabled(authenticatedUser)) {
    return null;
  }

  const buffer = watchedBuffer ?? EMPTY_BUFFER;
  const start = watchedTime ?? startTime;
  const duration = watchedDuration?.value ?? durationMins;

  const handleChange = (next: ScheduleBuffer) =>
    form.setValue("buffer", next, { shouldDirty: true });

  return (
    // `rainbow-stable` is what this card's stylesheet asks custom fields to
    // carry (see the "rainbow outline stability" block in styles/index.css):
    // it keeps the field on a stable layer instead of one that appears and
    // disappears around focus, the way every sibling field here already does.
    <div className="rainbow-stable flex w-full flex-col gap-2">
      <InputLabel htmlFor="buffer" className="text-left">
        Buffer
      </InputLabel>
      <ReservationBufferFields value={buffer} onChange={handleChange} />
      <ReservationBufferSummary
        start={start}
        end={addMinutes(start, duration)}
        buffer={buffer}
      />
    </div>
  );
};
